import { ProductBuilderMachineContext } from '../types';
import { getProductType } from './getProductType';
import { productPropertySummary } from './productPropertySummary';

type RollerblindLineItems = (
  context: ProductBuilderMachineContext,
) => {
  _ROOM: string;
  _MOUNTTYPE: string;
  _BLINDTYPE: string;
  _COLORCODE: string;
  _COLORCODE2: string;
  _CHAINCOLOR: string;
  _CHAINTYPE: string;
  _CONTROLTYPE: string;
  _FASCIAENDCAPCOLORID: string;
  _HDBRACKET: string;
  _HEADRAILMAT: string;
  _HEADRAILQTY: string;
  _HEADRAILCOLOR: string;
  _HEMBARCOLOR: string;
  _HEMBARTYPE: string;
  _KEYSTONE: string;
  _LIGHTBLOCKCOLORID: string;
  _LIGHTBLOCKMAT: string;
  _MATCHITEM: string;
  _PGMCODE: string;
  _PGMCODE2: string;
  _PULLCORDPOS1: string;
  _PULLCORDPOS2: string;
  _PULLCORDPOS3: string;
  _PULLCORDLENGTH: string;
  _REMARK: string;
  _ROLLERDIRECTION: string;
  _SIZETYPE: string;
  _VALANCETYPE: string;
  _VALRETURN: string;
  _VALLENGTH: string;
  _VALANCECOLOR: string;
  _WALLANCHOR: string;
  _WIDTH1: string;
  _WIDTH2: string;
  _WIDTH3: string;
  _WANDQTY: string;
  _dynamic_product: boolean;
  _SUPPLIER?: string;
  _COMPONENTS_COLOUR?: string;
  _CHAIN_COLOUR?: string;
  _BOTTOM_BAR_COLOUR?: string;
};

export const getRollerblindLineItems: RollerblindLineItems = context => {
  const { custom } = context;
  const { dayFabricPosition } = custom;

  const productSummaryCapture = productPropertySummary(context);
  const currentVariant = context.productWithMeasurements
    ? context.productWithMeasurements.data.filter(
        ({ attributes: { freight } }) => freight === context.custom.shipping,
      )
    : null;

  const baseProduct = context.products.filter(product =>
    product.variants.some(
      variant =>
        variant.id ===
        (currentVariant ? currentVariant[0].attributes.base_variant_id : null),
    ),
  );

  const secondBaseProduct =
    currentVariant && currentVariant.length > 1
      ? context.products.filter(product =>
          product.variants.some(
            variant =>
              variant.id ===
              (currentVariant
                ? currentVariant[1].attributes.base_variant_id
                : null),
          ),
        )
      : null;

  const colourName = baseProduct
    ? baseProduct[0].tags.find(tag => tag.startsWith('Colour:'))
    : '';

  const secondColourName = secondBaseProduct
    ? secondBaseProduct[0].tags.find(tag => tag.startsWith('Colour:'))
    : '';

  const dayProductIsBaseProduct =
    custom.colourDay && custom.colourDay === colourName
      ? true
      : custom.colourDay && custom.colourDay === secondColourName
      ? false
      : null;

  const baseProductDayFabricPositionOverride =
    dayProductIsBaseProduct !== false && dayFabricPosition !== undefined
      ? dayFabricPosition
      : dayProductIsBaseProduct === false && dayFabricPosition === 'Top'
      ? 'Bottom'
      : dayProductIsBaseProduct === false && dayFabricPosition === 'Bottom'
      ? 'Top'
      : null;

  const colourCode = baseProduct
    ? baseProduct[0].tags
        .find(tag => tag.includes('Colour Code'))
        ?.replace('Colour Code: ', '')
    : '';

  const secondColourCode = secondBaseProduct
    ? secondBaseProduct[0].tags
        .find(tag => tag.includes('Colour Code'))
        ?.replace('Colour Code: ', '')
    : '';

  const pgmCode = baseProduct
    ? baseProduct[0].tags
        .find(tag => tag.includes('PGMCODE'))
        ?.replace('PGMCODE:', '')
    : '';

  const pgmCode2 = secondBaseProduct
    ? secondBaseProduct[0].tags
        .find(tag => tag.includes('PGMCODE'))
        ?.replace('PGMCODE:', '')
    : '';

  const bracketColour = baseProduct
    ? baseProduct[0].tags
        .find(tag => tag.includes('Bracket Colour'))
        ?.replace('Bracket Colour:', '')
    : '';

  const bracketDisplayColour =
    bracketColour && bracketColour.trim() === 'White'
      ? '3058'
      : bracketColour && bracketColour.trim() === 'Silver'
      ? '2129'
      : bracketColour && bracketColour.trim() === 'Black'
      ? '2208'
      : bracketColour && bracketColour.trim() === 'Beige'
      ? '2021'
      : '';

  let controlType, chainType, rollDirection, rollDirectionWatson;
  const width = context.custom.measurements.split('x')[0];

  const isStainless =
    (custom.chainColour && custom.chainColour.toLowerCase().includes('stainless')) ||
    (custom.chainType && custom.chainType.toLowerCase().includes('stainless'));

  chainType = isStainless ? 'CT002' : 'CT001';
  rollDirection =
    custom.rollDirection === 'Front Roll' &&
    !window.location.pathname.includes('roller-day-night')
      ? 'Reverse'
      : 'Standard';
  
  rollDirectionWatson =
    custom.rollDirection === 'Front Roll' &&
      !window.location.pathname.includes('roller-day-night')
      ? 'Standard'
      : 'Reverse';

  let rollerPremiumCorded = context.products.filter(ele =>
    ele.tags?.includes(`Material: ${context?.custom?.materialType}`) &&
    ele.tags?.includes(`${context?.custom?.colour}`) &&
    ele?.metafields?.supplier !== undefined
  )

  switch (true) {
    case context.custom.driveControl === 'Drive Control: Chain' &&
      typeof context.custom.smartRelease === 'number':
      controlType = 'SmartRelease';
      break;
    case context.custom.driveControl === 'Drive Control: Chain' &&
      typeof context.custom.smartRelease === 'string' &&
      context.custom.smartRelease.toLowerCase() === 'no':
      controlType = 'Manual';
      break;
    case context.custom.driveControl === 'Drive Control: Chain' &&
      secondBaseProduct !== null:
      controlType = 'Manual';
      break;
    case context.products[0].type === 'Roller Blind Essentials Chain':
      controlType = 'Manual';
      break;
    case context.products[0].type === 'Roller Blind Premium Chain':
      controlType = 'Corded';
      break;
    case context.products[0].type === 'Roller Blind Essentials Chain' && rollerPremiumCorded?.length > 0:
      controlType = 'Corded';
      break;
    case context.products[0].type === 'Roller Blind Single Chain' && rollerPremiumCorded?.length > 0:
      controlType = 'Corded';
      break;
    default:
      controlType = 'Cordless';
  }

  const chainSide =
    (context.custom.driveControl === 'Drive Control: Chain' &&
      context.custom.chainPosition === 'Right') ||
    (context.custom.driveControl === 'Drive Control: Chain' &&
      window.location.pathname.includes('roller-day-night'))
      ? 'R'
      : context.custom.driveControl === 'Drive Control: Chain' &&
        context.custom.chainPosition !== 'Right' &&
        !window.location.pathname.includes('roller-day-night')
      ? 'L'
      : '';

  return {
    _ROOM: context.custom.roomName,
    _MOUNTTYPE: context.custom.fitting === 'Reveal Fit' ? 'IN' : 'OUT',
    _BLINDTYPE: currentVariant && currentVariant.length > 1 ? 'dual' : 'single',
    _COLORCODE:
      (colourCode && baseProductDayFabricPositionOverride === null) ||
      (colourCode && baseProductDayFabricPositionOverride === 'Top')
        ? colourCode
        : baseProductDayFabricPositionOverride !== null &&
          baseProductDayFabricPositionOverride === 'Bottom' &&
          secondColourCode
        ? secondColourCode
        : '', // primary colour, or front colour if day/night
    _COLORCODE2:
      secondColourCode &&
      baseProductDayFabricPositionOverride !== null &&
      baseProductDayFabricPositionOverride === 'Top'
        ? secondColourCode
        : colourCode &&
          baseProductDayFabricPositionOverride !== null &&
          baseProductDayFabricPositionOverride === 'Bottom'
        ? colourCode
        : '', // Secondary colour if day/night
    _CHAINCOLOR:
      controlType === 'Cordless' ||
      custom.chainColour === 'Stainless Steel' ||
      custom.chainType === 'Stainless Steel'
        ? ''
        : 'TD01', // plastic = CT001, steel = CT002
    _CHAINTYPE: controlType === 'Cordless' ? '' : chainType,
    _CONTROLTYPE: controlType,
    _FASCIAENDCAPCOLORID:
      typeof context.custom.fascia === 'string' &&
      context.custom.fascia.toLowerCase() !== 'no'
        ? 'TD01'
        : '',
    _HDBRACKET: '', // Veneta backend
    _HEADRAILMAT: isStainless ? 'SS' : '', // Only when customer selects Stainless Steel in the builder
    _HEADRAILQTY: '', // Veneta backend
    _HEADRAILCOLOR: 'TD01',
    _HEMBARCOLOR: '', // Changed to be left blank instead of default value as per Veneta's request
    _HEMBARTYPE: 'HB005',
    _KEYSTONE: width > 2286 && context.custom.fascia !== 'No' ? 'Y' : '',
    _LIGHTBLOCKCOLORID:
      typeof custom.lightGuards !== 'undefined' &&
      typeof custom.lightGuards === 'number' &&
      bracketDisplayColour
        ? bracketDisplayColour
        : '',
    _LIGHTBLOCKMAT:
      typeof custom.lightGuards !== 'undefined' &&
      typeof custom.lightGuards === 'number'
        ? 'AL'
        : '',
    _MATCHITEM: '', // empty
    _PGMCODE:
      (pgmCode && baseProductDayFabricPositionOverride === null) ||
      (pgmCode && baseProductDayFabricPositionOverride === 'Top')
        ? pgmCode
        : baseProductDayFabricPositionOverride !== null &&
          baseProductDayFabricPositionOverride === 'Bottom' &&
          pgmCode2
        ? pgmCode2
        : '',
    _PGMCODE2:
      pgmCode2 &&
      baseProductDayFabricPositionOverride !== null &&
      baseProductDayFabricPositionOverride === 'Top'
        ? pgmCode2
        : pgmCode &&
          baseProductDayFabricPositionOverride !== null &&
          baseProductDayFabricPositionOverride === 'Bottom'
        ? pgmCode
        : '',
    _PULLCORDPOS1: chainSide, // Primary chain side, if day/night this is front
    _PULLCORDPOS2:
      context.custom.driveControl === 'Drive Control: Chain' &&
      window.location.pathname.includes('roller-day-night')
        ? 'L'
        : '', // Secondary chain side, if day/night this is back
    _PULLCORDPOS3: '', // Veneta only
    _PULLCORDLENGTH: '', // Veneta only
    _REMARK: '', // empty
    _ROLLERDIRECTION: rollerPremiumCorded.length > 0 ? rollDirectionWatson : rollDirection,
    _SIZETYPE: context.custom.fitting === 'Reveal Fit' ? 'WS' : 'MFF',
    _VALANCETYPE:
      (typeof context.custom.smartRelease !== 'undefined' &&
        typeof context.custom.smartRelease === 'number') ||
      window.location.pathname.includes('roller-day-night')
        ? 'F009'
        : 'NO',
    _VALRETURN: '', // empty
    _VALLENGTH: '', // empty
    _VALANCECOLOR:
      typeof context.custom.fascia !== 'undefined' &&
      typeof context.custom.fascia === 'number'
        ? 'TD01' // default will be TD01
        : '',
    _WALLANCHOR: 'N',
    _WIDTH1: '', // backend only
    _WIDTH2: '', // backend only
    _WIDTH3: '', // backened only
    _WANDQTY: '',
    _dynamic_product: true,
    ...rollerPremiumCorded?.length > 0 ? {
      _SUPPLIER: `${rollerPremiumCorded[0].metafields.supplier}`,
      _COMPONENTS_COLOUR: custom.componentsColour,
      _CHAIN_COLOUR: custom.chainColour,
      _BOTTOM_BAR_COLOUR: custom.bottomBarColour
    } : null,
    ...productSummaryCapture,
  };
};
